import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { Scope } from '@utils'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'

interface SendContactRequestData {
  recipientId: string
  scope: Scope
}

const sendContactRequest = async (data: SendContactRequestData): Promise<undefined> => {
  const { data: responseData } = await api.post<undefined>('/contact-requests', data)
  return responseData
}

export const useSendContactRequest = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<undefined, Error, SendContactRequestData>({
    mutationFn: sendContactRequest,
    onSuccess: async () => {
      showSuccessToast(t('toast.contactRequestSent'), t('toast.contactRequestSentMessage'))
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['contact-requests'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['suggested-contacts'],
        }),
      ])
    },
    // On the mutation itself, not on each mutate() call: those callbacks are
    // lost once the screen is closed, and a refused request said nothing.
    onError: async (error) => {
      showApiErrorToast(t, error)
      const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code
      if (code === 'Networking.ContactRequest.AlreadyExists') {
        // The request is already waiting: read the list again so the button
        // says « Demande envoyée » instead of offering « Ajouter » once more.
        await queryClient.invalidateQueries({ queryKey: ['contact-requests'] })
      }
    },
  })
}
